import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import type { SurveyJson } from "@digital-twins/shared-types";
import { useQuestionnaireCoverage } from "@/features/studies/questionnaire/hooks/useQuestionnaireCoverage";
import type { CoverageEntry } from "@/features/studies/questionnaire/components/TopicCoverageChip";

function isFlagged(entry: CoverageEntry | undefined): boolean {
  if (!entry || entry === "computing") return false;
  return entry.level === "partial" || entry.level === "out_of_domain";
}

function questionIdsFromSurvey(survey: SurveyJson): Array<string> {
  const pages = survey.pages ?? [];
  const ids: Array<string> = [];
  for (const page of pages) {
    for (const element of page.elements ?? []) {
      if (typeof element.name === "string" && element.name.length > 0) {
        ids.push(element.name);
      }
    }
  }
  return ids;
}

interface ReviewTopicCoverageNoticeProps {
  questionnaireDraftId: string | null;
  reviewedRevision: number | null;
  editedSurveyJson: SurveyJson;
}

/** Advisory coverage warnings on Review (same assess-coverage verdicts as the builder). */
export function ReviewTopicCoverageNotice({
  questionnaireDraftId,
  reviewedRevision,
  editedSurveyJson,
}: ReviewTopicCoverageNoticeProps) {
  const { t } = useTranslation("app");
  const questionIds = useMemo(
    () => questionIdsFromSurvey(editedSurveyJson),
    [editedSurveyJson],
  );
  const coverageByQuestionId = useQuestionnaireCoverage({
    draftId: questionnaireDraftId,
    revision: reviewedRevision ?? undefined,
    questionIds,
  });

  const flagged = useMemo(() => {
    const out: Array<{ id: string; level: "partial" | "out_of_domain" }> = [];
    for (const [id, entry] of coverageByQuestionId) {
      if (entry === "computing" || !isFlagged(entry)) continue;
      out.push({ id, level: entry.level as "partial" | "out_of_domain" });
    }
    return out;
  }, [coverageByQuestionId]);

  if (flagged.length === 0) return null;

  return (
    <div
      className="mb-4 flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-foreground"
      data-testid="review-topic-coverage-notice"
      role="status"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
      <div className="space-y-1">
        <p className="font-medium">{t("creation.reviewCoverageWarningTitle")}</p>
        <ul className="list-disc space-y-0.5 pl-5 text-muted-foreground">
          {flagged.map(({ level }) => (
            <li key={level}>
              {level === "partial"
                ? t("questionnaire.partialCoverage")
                : t("questionnaire.unsupportedByAudience")}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
